import axios from 'axios';

import { store } from '@/app/store';
import { env } from '@/config/env';
import { refreshAuthSession, shouldClearSessionOnRefreshFailure } from '@/features/auth/lib/sessionRefresh';
import { loginSuccess, logout } from '@/features/auth/store/authSlice';
import { reportClientError, createCorrelationId } from '@/features/monitoring/lib/telemetry';
import { AppError } from '@/lib/error';
import { normalizeApiPayload } from '@/utils/normalizeApi';

export const apiClient = axios.create({
  baseURL: env.VITE_API_BASE_URL,
  timeout: 10000
});

apiClient.interceptors.response.use(
  response => {
    response.data = normalizeApiPayload(response.data);
    return response;
  },
  async error => {
    const correlationId = error.config?.headers?.['x-correlation-id'] as string | undefined;
    const status = error.response?.status as number | undefined;
    const normalized = normalizeApiPayload(error.response?.data);
    const message = normalized?.message ?? normalized?.detail ?? normalized?.title ?? 'API request failed';
    const method = error.config?.method as string | undefined;
    const url = error.config?.url as string | undefined;
    const originalRequest = error.config as
      | (typeof error.config & {
          _retry?: boolean;
        })
      | undefined;

    const isAuthRoute = String(url ?? '').includes('/auth/login') || String(url ?? '').includes('/auth/refresh') || String(url ?? '').includes('/auth/logout');

    if (status === 401 && originalRequest && !originalRequest._retry && !isAuthRoute) {
      const state = store.getState();
      const refreshToken = state.auth.refreshToken;

      if (refreshToken) {
        originalRequest._retry = true;

        try {
          const refreshed = await refreshAuthSession(refreshToken);
          store.dispatch(loginSuccess({ ...refreshed, rememberMe: state.auth.rememberMe }));
          originalRequest.headers = originalRequest.headers ?? {};
          originalRequest.headers.Authorization = `Bearer ${refreshed.tokens.accessToken}`;
          return apiClient(originalRequest);
        } catch (refreshError) {
          if (shouldClearSessionOnRefreshFailure(refreshError)) {
            store.dispatch(logout());
          }
        }
      }
    }

    if (!String(url ?? '').includes('/telemetry/errors')) {
      void reportClientError({
        source: 'frontend-api',
        category: 'http-error',
        message,
        correlationId,
        statusCode: status,
        httpMethod: method?.toUpperCase(),
        route: url,
        requestContext: error.config?.data,
        responseContext: normalized
      });
    }

    return Promise.reject(
      new AppError(message, status, {
        method: method?.toUpperCase(),
        url,
        correlationId,
        responsePayload: normalized,
        source: 'frontend-api'
      })
    );
  }
);

apiClient.interceptors.request.use(config => {
  const state = store.getState();
  const accessToken = state.auth.accessToken;
  const clinicId = state.auth.clinicId ?? 'clinic-main';
  const actor = state.auth.userName ?? 'web-user';
  const correlationId = createCorrelationId();

  config.headers.set('x-clinic-id', clinicId);
  config.headers.set('x-user-id', actor);
  config.headers.set('x-correlation-id', correlationId);

  if (accessToken) {
    config.headers.set('Authorization', `Bearer ${accessToken}`);
  }

  if (env.VITE_FUNCTIONS_KEY) {
    config.headers.set('x-functions-key', env.VITE_FUNCTIONS_KEY);
  }

  return config;
});
